import type { Driver } from '@multirx/shared';
import { driverPower } from '@multirx/shared/types/driver.js';
import { labelOwnDriver } from '../auth/ownDriverLabel.js';
import { nationalityFlagMarkup } from '../components/nationalityFlag.js';

export type DriverListSort = 'name-asc' | 'name-desc' | 'power-asc' | 'power-desc';

export type DriverPowerBand = 'all' | '2-4' | '4-6' | '6-8' | '8-10';

export interface DriverListFilters {
  search: string;
  powerBand: DriverPowerBand;
  sort: DriverListSort;
}

export const DEFAULT_DRIVER_LIST_FILTERS: DriverListFilters = {
  search: '',
  powerBand: 'all',
  sort: 'power-desc',
};

const POWER_BANDS: Record<DriverPowerBand, { min: number; max: number; label: string } | null> = {
  all: null,
  '2-4': { min: 2, max: 4, label: '2–4★' },
  '4-6': { min: 4, max: 6, label: '4–6★' },
  '6-8': { min: 6, max: 8, label: '6–8★' },
  '8-10': { min: 8, max: 10, label: '8–10★' },
};

function driverDisplayName(d: Driver): string {
  return `${d.firstName} ${d.lastName}`.trim();
}

export function applyDriverListFilters(drivers: Driver[], filters: DriverListFilters): Driver[] {
  const q = filters.search.trim().toLowerCase();
  const band = POWER_BANDS[filters.powerBand];

  let list = drivers.filter((d) => {
    const name = driverDisplayName(d).toLowerCase();
    if (
      q &&
      !name.includes(q) &&
      !d.firstName.toLowerCase().includes(q) &&
      !d.lastName.toLowerCase().includes(q) &&
      !String(d.id ?? '').includes(q)
    ) {
      return false;
    }
    const power = driverPower(d);
    if (band && (power < band.min || power > band.max)) {
      return false;
    }
    return true;
  });

  list = [...list].sort((a, b) => {
    switch (filters.sort) {
      case 'name-asc':
        return driverDisplayName(a).localeCompare(driverDisplayName(b), 'fr');
      case 'name-desc':
        return driverDisplayName(b).localeCompare(driverDisplayName(a), 'fr');
      case 'power-asc':
        return driverPower(a) - driverPower(b) || (a.id ?? 0) - (b.id ?? 0);
      case 'power-desc':
      default:
        return driverPower(b) - driverPower(a) || (a.id ?? 0) - (b.id ?? 0);
    }
  });

  return list;
}

export interface DriverListToolbarHandle {
  getFilters: () => DriverListFilters;
  setFilters: (filters: DriverListFilters) => void;
}

export function mountDriverListToolbar(
  container: HTMLElement,
  initial: DriverListFilters,
  onChange: (filters: DriverListFilters) => void
): DriverListToolbarHandle {
  container.innerHTML = '';
  container.className = 'garage-sidebar-toolbar';

  let filters = { ...initial };

  const searchInput = document.createElement('input');
  searchInput.type = 'search';
  searchInput.className = 'garage-toolbar-search';
  searchInput.placeholder = 'Rechercher par nom ou n°…';
  searchInput.autocomplete = 'off';
  searchInput.value = filters.search;

  const rowFilters = document.createElement('div');
  rowFilters.className = 'garage-toolbar-row';

  const powerLabel = document.createElement('label');
  powerLabel.className = 'garage-toolbar-label';
  powerLabel.textContent = 'Niveau';

  const powerSelect = document.createElement('select');
  powerSelect.className = 'garage-toolbar-select';
  powerSelect.setAttribute('aria-label', 'Filtrer par niveau total');
  for (const [key, meta] of Object.entries(POWER_BANDS)) {
    const opt = document.createElement('option');
    opt.value = key;
    opt.textContent = meta ? meta.label : 'Toutes';
    powerSelect.appendChild(opt);
  }
  powerSelect.value = filters.powerBand;

  const sortLabel = document.createElement('label');
  sortLabel.className = 'garage-toolbar-label';
  sortLabel.textContent = 'Tri';

  const sortSelect = document.createElement('select');
  sortSelect.className = 'garage-toolbar-select';
  sortSelect.setAttribute('aria-label', 'Trier la liste');
  sortSelect.innerHTML = `
    <option value="power-desc">Niveau ↓</option>
    <option value="power-asc">Niveau ↑</option>
    <option value="name-asc">Nom A→Z</option>
    <option value="name-desc">Nom Z→A</option>`;
  sortSelect.value = filters.sort;

  powerLabel.appendChild(powerSelect);
  sortLabel.appendChild(sortSelect);
  rowFilters.append(powerLabel, sortLabel);

  const countEl = document.createElement('p');
  countEl.className = 'garage-toolbar-count';
  countEl.setAttribute('aria-live', 'polite');

  function emit(): void {
    filters = {
      search: searchInput.value,
      powerBand: powerSelect.value as DriverPowerBand,
      sort: sortSelect.value as DriverListSort,
    };
    onChange(filters);
  }

  searchInput.addEventListener('input', emit);
  powerSelect.addEventListener('change', emit);
  sortSelect.addEventListener('change', emit);

  container.append(searchInput, rowFilters, countEl);

  return {
    getFilters: () => ({ ...filters }),
    setFilters: (next) => {
      filters = { ...next };
      searchInput.value = filters.search;
      powerSelect.value = filters.powerBand;
      sortSelect.value = filters.sort;
    },
  };
}

export function updateDriverListCount(
  toolbarRoot: HTMLElement,
  visible: number,
  total: number
): void {
  const countEl = toolbarRoot.querySelector('.garage-toolbar-count');
  if (!countEl) return;
  if (visible === total) {
    countEl.textContent = `${total} pilote${total !== 1 ? 's' : ''}`;
  } else {
    countEl.textContent = `${visible} / ${total} pilote${total !== 1 ? 's' : ''}`;
  }
}

function appendDriverRow(
  list: HTMLElement,
  d: Driver,
  onSelect: (id: number) => void,
  selectedId: number | null
): void {
  const total = driverPower(d);
  const name = driverDisplayName(d);
  const displayName = labelOwnDriver(name, d.id);
  const row = document.createElement('button');
  row.type = 'button';
  row.className = 'garage-list-row garage-list-row--driver';
  row.setAttribute('role', 'option');
  row.dataset.id = String(d.id);
  row.title = `${displayName} — Pilotage ${d.pilotage}★, Défense ${d.defense}★`;

  if (selectedId !== null && d.id === selectedId) {
    row.classList.add('garage-list-row--selected');
    row.setAttribute('aria-selected', 'true');
  } else {
    row.setAttribute('aria-selected', 'false');
  }

  row.innerHTML = `
      <span class="garage-list-row__flag">${nationalityFlagMarkup(d.nationality)}</span>
      <span class="garage-list-row__id">#${d.id}</span>
      <span class="garage-list-row__name">
        <span class="garage-list-row__name-text">${escapeHtml(displayName)}</span>
        <span class="garage-list-row__stats" aria-hidden="true">${d.pilotage}★ · ${d.defense}★</span>
      </span>
      <span class="garage-list-row__power">${total}★</span>`;

  row.addEventListener('click', () => onSelect(d.id!));
  list.appendChild(row);
}

function appendDriverSection(
  parent: HTMLElement,
  title: string,
  drivers: Driver[],
  onSelect: (id: number) => void,
  selectedId: number | null
): void {
  if (drivers.length === 0) return;
  const section = document.createElement('section');
  section.className = 'garage-list-section';
  const heading = document.createElement('h3');
  heading.className = 'garage-list-section__title';
  heading.textContent = title;
  const list = document.createElement('div');
  list.className = 'garage-list';
  list.setAttribute('role', 'listbox');
  list.setAttribute('aria-label', title);
  for (const d of drivers) {
    appendDriverRow(list, d, onSelect, selectedId);
  }
  section.append(heading, list);
  parent.appendChild(section);
}

export function renderDriverList(
  container: HTMLElement,
  drivers: Driver[],
  onSelect: (id: number) => void,
  selectedId: number | null = null,
  options?: { emptyFiltered?: boolean }
): void {
  container.innerHTML = '';

  if (drivers.length === 0) {
    container.innerHTML = options?.emptyFiltered
      ? `<div class="garage-list-empty"><p>Aucun résultat pour ces filtres.</p></div>`
      : `
      <div class="circuits-empty">
        <span class="circuits-empty-icon" aria-hidden="true">🏎️</span>
        <p>Aucun pilote enregistré.</p>
        <p class="circuits-empty-hint">Cliquez sur « Nouveau » pour en créer un.</p>
      </div>`;
    return;
  }

  const playerDrivers = drivers.filter((d) => d.ownerUserId != null);
  const aiDrivers = drivers.filter((d) => d.ownerUserId == null);

  const wrap = document.createElement('div');
  wrap.className = 'garage-list-sections';
  if (playerDrivers.length > 0 && aiDrivers.length > 0) {
    appendDriverSection(wrap, 'Pilotes des joueurs', playerDrivers, onSelect, selectedId);
    appendDriverSection(wrap, 'Pilotes IA', aiDrivers, onSelect, selectedId);
  } else if (playerDrivers.length > 0) {
    appendDriverSection(wrap, 'Pilotes des joueurs', playerDrivers, onSelect, selectedId);
  } else {
    appendDriverSection(wrap, 'Pilotes IA', aiDrivers, onSelect, selectedId);
  }
  container.appendChild(wrap);
}

export function syncDriverListSelection(container: HTMLElement, selectedId: number | null): void {
  container.querySelectorAll('.garage-list-row').forEach((el) => {
    const row = el as HTMLElement;
    const selected = row.dataset.id === String(selectedId);
    row.classList.toggle('garage-list-row--selected', selected);
    row.setAttribute('aria-selected', selected ? 'true' : 'false');
  });
}

export function setDriverListLoading(container: HTMLElement, loading: boolean): void {
  if (loading) {
    container.innerHTML =
      '<div class="circuits-loading circuits-loading--compact"><span class="spinner"></span><p>Chargement…</p></div>';
  }
}

export function setDriverListError(container: HTMLElement, message: string): void {
  container.innerHTML = `
    <div class="circuits-error">
      <span class="circuits-error-icon" aria-hidden="true">⚠️</span>
      <p>${escapeHtml(message)}</p>
    </div>`;
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}
